import { css, styled } from 'canoe';
import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { ExperimentsPlaceholder } from '@candu-ai/experiments/ui';
import { useEventing } from 'commons/eventing';
import { useOutsideClick } from 'commons/hooks';
import Node from 'craft/components/Node';
import { BuiltInComponents } from 'craft/constants';
import { VIEWPORT_CONTAINER_ID } from 'craft/containers/Viewport';
import {
  setDocumentDom,
  setScrollableBodyDom,
  useCurrentSnapshot,
  usePlacementMode,
  useToolboxState,
} from 'craft/state';
import { Resolver, ToolboxComponentSections } from 'craft/types';
import { hasDescendants } from 'craft/utils';
import { getSelectorFromIframe } from 'craft/utils/snapshot-iframe';

import { Placeholder } from './Placeholder';
// avoiding circular dependency
import { DOCUMENT_ID } from './constants';

export const DocumentNode = styled(Node)`
  ${({ theme }) => css`
    overflow: auto;
    position: relative;

    // match outline of all other selected nodes

    &.selected {
      outline: 1px solid ${theme.colors.BrandPurpleL10} !important;
    }
  `}
`;

// TODO add better props
export const Document: Resolver = ({ children, className, ...otherProps }: any) => {
  const domRef = useRef<HTMLElement | null>(null);
  const dispatch = useDispatch();
  const { snapshot, placement } = useCurrentSnapshot();
  const hasChildren = hasDescendants(children);
  const params = useParams();
  const { previewSelector } = usePlacementMode();
  const isExperiment = !!params?.variantId;

  const { track } = useEventing();
  const { minimizeToolboxOnSnapshotClick } = useToolboxState();

  const placeholderComponent = isExperiment ? <ExperimentsPlaceholder /> : <Placeholder />;

  const documentPlacement =
    snapshot && placement ? previewSelector || placement?.selector : placement?.selector;
  const placementSelectorEl = documentPlacement && getSelectorFromIframe(documentPlacement);

  useEffect(() => {
    // save document ref to the store
    dispatch(setDocumentDom(domRef));
    // save document root's scrollable body ref to the store
    dispatch(setScrollableBodyDom(domRef));
  }, []);

  // @ts-ignore
  useOutsideClick(domRef?.current, (e) => {
    // @ts-ignore
    const targetId = e?.target?.id;

    if (targetId !== VIEWPORT_CONTAINER_ID) {
      // only consider an outside click when it's triggered from inside the canvas
      return;
    }

    if (minimizeToolboxOnSnapshotClick()) {
      track('editor.dockedToolbox.snapshotClose');
    }
  });

  if (snapshot && (!documentPlacement || !placementSelectorEl)) {
    return null;
  }

  return (
    <DocumentNode
      is="div"
      size="lg"
      id={DOCUMENT_ID}
      innerRef={(dom: any) => {
        domRef.current = dom;
      }}
      className={`candu-document ${className}`.trim()}
      css={{
        padding: 16,
        flexDirection: 'column',
        backgroundColor: snapshot ? '' : 'white',
      }}
      style={{
        display: 'none',
        flex: 1,
      }}
      data-cy="node-document"
      {...otherProps}
    >
      {hasChildren ? children : placeholderComponent}
    </DocumentNode>
  );
};

Document.craft = {
  name: BuiltInComponents.Document,
  isCanvas: true,
};

Document.toolbox = {
  schema: {
    [ToolboxComponentSections.Size]: {
      hasOverflow: true,
    },
  },
};
